import { Injectable } from '@angular/core';
import { saveAs } from 'file-saver/FileSaver';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { Ambassador } from '@models/subscription.model';
import { GeneralAction } from '@models/models';

@Injectable()
export class FileDownloadDataServiceProvider {
	constructor(private http: HttpClient) {}

	downloadFileDynamic(
		action: GeneralAction,
		body: any
	): Observable<HttpResponse<Blob>> {
		return this.http
			.post(action.url, body, { responseType: 'blob', observe: 'response' })
			.pipe(
				tap((response: HttpResponse<Blob>): void =>
					this.writeToFileSystem(response)
				)
			);
	}

	saveFileFromJob(fileId, jobId): Observable<HttpResponse<Blob>> {
		const url = `/rest/user/jobs/${jobId}/attachments/${fileId}`;
		return this.http.get(url, { responseType: 'blob', observe: 'response' });
	}

	saveClients(): Promise<void> {
		const url = '/protected/rest/clients/export-clients';
		return this.http
			.get(url, { responseType: 'blob', observe: 'response' })
			.toPromise()
			.then((response: HttpResponse<Blob>): void =>
				this.saveClientsToFileSystem(response)
			);
	}

	saveClientsTemplate(): Promise<void> {
		const url = '/protected/rest/clients/download-csv-template';
		return this.http
			.get(url, { responseType: 'blob', observe: 'response' })
			.toPromise()
			.then((response: HttpResponse<Blob>): void =>
				this.saveClientsTemplateToFileSystem(response)
			);
	}

	savePaymentsCSV(response): void {
		this.savePaymentsToFileSystemCSV(response);
	}

	savePaymentsXLS(response): void {
		this.savePaymentsToFileSystemXLS(response);
	}

	saveAmbassadorReferrals(response, ambassador): void {
		this.saveAmbassadorReferralsXLS(response, ambassador);
	}

	downloadPdf(
		linkToken: string,
		id: number,
		documentType?: string
	): Promise<void> {
		let url;
		switch (documentType) {
			case 'invoice':
				url = `/api/public/v1/invoice/${linkToken}/${id}/download-pdf`;
				break;
			case 'quote':
				url = `/api/public/v1/quotes/${linkToken}/${id}/download-pdf`;
				break;
			default:
				url = `/api/public/v1/quotes/${linkToken}/${id}/download-pdf`;
		}
		return this.http
			.get(url, { responseType: 'blob', observe: 'response' })
			.toPromise()
			.then((response: HttpResponse<Blob>): void =>
				this.saveToFileSystem(response)
			);
	}

	downloadPDF(url: string, name: string, id: number): Observable<Object> {
		return this.http
			.post(url, {}, { responseType: 'blob', observe: 'response' })
			.pipe(
				tap((response: HttpResponse<Blob>): void =>
					this.savePdf(response, name, id)
				)
			);
	}

	saveContractPdf(url: string, fileName: string): Promise<void> {
		return this.http
			.post(url, {}, { responseType: 'blob', observe: 'response' })
			.toPromise()
			.then((response: HttpResponse<Blob>): void => {
				const blob = new Blob([response.body], { type: response.body.type });
				saveAs(blob, fileName);
			});
	}

	savePdfFile(url: string): Promise<void> {
		return this.http
			.post(url, {}, { responseType: 'blob', observe: 'response' })
			.toPromise()
			.then((response: HttpResponse<Blob>): void => {
				this.saveToFileSystem(response);
			});
	}

	saveToFileSystem(response: HttpResponse<Blob>): void {
		const contentDispositionHeader: string = response.headers.get(
			'Content-Disposition'
		);
		const parts: string[] = contentDispositionHeader.split(';');
		const filename = parts[1].split('=')[1];
		const blob = new Blob([response.body], { type: response.body.type });
		saveAs(blob, filename);
	}

	saveFile(response: HttpResponse<Blob>, name: string, fileType): void {
		const blob = new Blob([response.body], { type: response.body.type });
		saveAs(blob, `${name}.${fileType}`);
	}

	private savePdf(
		response: HttpResponse<Blob>,
		name: string,
		id: number
	): void {
		const blob = new Blob([response.body], { type: response.body.type });
		saveAs(blob, `${name}-${id}`);
	}

	private saveClientsToFileSystem(response: HttpResponse<Blob>): void {
		const blob = new Blob([response.body], { type: response.body.type });
		saveAs(blob, 'export_clients.csv');
	}

	private savePaymentsToFileSystemCSV(response: HttpResponse<Blob>): void {
		const blob = new Blob([response.body], { type: response.body.type });
		saveAs(blob, 'export_payments.csv');
	}

	private savePaymentsToFileSystemXLS(response: HttpResponse<Blob>): void {
		const blob = new Blob([response.body], { type: response.body.type });
		saveAs(blob, 'export_payments.xls');
	}

	private saveAmbassadorReferralsXLS(
		response: HttpResponse<Blob>,
		ambassador: Ambassador
	): void {
		const blob = new Blob([response.body], { type: response.body.type });
		saveAs(
			blob,
			`${ambassador.firstName.replace(/\s/g, '_')}_(${
				ambassador.email
			})_Referrals.xls`
		);
	}

	private saveClientsTemplateToFileSystem(response: HttpResponse<Blob>): void {
		const blob = new Blob([response.body], { type: response.body.type });
		saveAs(blob, 'import_clients_template.csv');
	}

	private writeToFileSystem(response: HttpResponse<Blob>): void {
		const blob = new Blob([response.body], { type: response.body.type });
		saveAs(blob, this.findFileName(response));
	}

	private findFileName(response: HttpResponse<Blob>): string {
		const value = response.headers.get('content-disposition');
		return value ? value.slice(value.indexOf('=') + 1) : 'document';
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=os2z8rc99lf!./shared/services/files/file-download-service/file-download.data.service.ts